import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { TabletButton } from '@campfire/tablet-button';
import { SearchField } from '../../../../../common/inputs/SearchField';
import { FullscreenDialog } from '../../../../../common/dialogs/FullscreenDialog';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { GET_SUB_ACCOUNT_ENTITIES } from './enterprise-sub-account-entities.gql';
import { entityTypeHeading, entityTypeIcon } from './enterprise-entity-labels';
import {
  GetSubAccountEntities,
  GetSubAccountEntitiesVariables,
  GetSubAccountEntities_vm_enterpriseSubAccountEntities as SubAccountEntity,
} from './__generated__/GetSubAccountEntities';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sectionHeading: {
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      color: theme.color.grey.neutral200,
      paddingTop: '1.25rem',
      paddingBottom: '0.25rem',
    },
    sectionBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    typeIcon: {
      fontSize: '16px',
      color: theme.color.grey.neutral200,
      marginRight: '8px',
    },
    relatedButton: {
      borderRadius: 4,
      textTransform: 'none' as const,
      fontSize: '12px',
      whiteSpace: 'nowrap' as const,
    },
    selectAll: {
      textTransform: 'none' as const,
      fontSize: '13px',
    },
    name: {
      fontSize: '14px',
      color: theme.color.grey.neutralBrand900,
    },
    bodyPanel: {
      fontSize: '14px',
      color: '#808080',
    },
  })
);

interface Props {
  open: boolean;
  subTenantId: string;
  subAccountName: string;
  onClose: () => void;
  onAdded: () => void;
}

export const AddEntitiesDialog = (props: Props) => {
  const { open, subTenantId, subAccountName, onClose, onAdded } = props;
  const classes = useStyles();
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const campfireMutation = useCampfireFetch({ defer: true });

  const [selected, setSelected] = React.useState<Record<string, string[]>>({});
  const [saving, setSaving] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [expandedTypes, setExpandedTypes] = React.useState<string[]>([]);

  const COLLAPSED_LIMIT = 5;

  const { data, loading } = useCampfireQuery<GetSubAccountEntities, GetSubAccountEntitiesVariables>(
    GET_SUB_ACCOUNT_ENTITIES,
    { options: { variables: { subTenantId } } }
  );

  const hasLoaded = data !== undefined;
  const entities = data?.vm.enterpriseSubAccountEntities ?? [];
  const term = search.trim().toLowerCase();
  const available = entities
    .filter((entity) => !entity.isMapped)
    .filter((entity) => term === '' || entity.name.toLowerCase().includes(term));

  const groupedTypes = available
    .map((entity) => entity.entityType)
    .filter((entityType, index, all) => all.indexOf(entityType) === index);

  const selectedCount = Object.values(selected).reduce((total, ids) => total + ids.length, 0);

  const toggle = (entity: SubAccountEntity) => {
    setSelected((current) => {
      const ids = current[entity.entityType] ?? [];
      const next = ids.includes(entity.masterEntityId)
        ? ids.filter((id) => id !== entity.masterEntityId)
        : [...ids, entity.masterEntityId];

      return { ...current, [entity.entityType]: next };
    });
  };

  const isChecked = (entity: SubAccountEntity) => (selected[entity.entityType] ?? []).includes(entity.masterEntityId);

  const availableOfType = (entityType: string) => available.filter((entity) => entity.entityType === entityType);

  const allSelectedOfType = (entityType: string) => {
    const ofType = availableOfType(entityType);

    return ofType.length > 0 && ofType.every((entity) => isChecked(entity));
  };

  const visibleOfType = (entityType: string) => {
    const ofType = availableOfType(entityType);

    return isExpanded(entityType) ? ofType : ofType.slice(0, COLLAPSED_LIMIT);
  };

  const hiddenCountOfType = (entityType: string) =>
    availableOfType(entityType).length - visibleOfType(entityType).length;

  const isExpanded = (entityType: string) => expandedTypes.includes(entityType) || term !== '';

  const toggleExpanded = (entityType: string) =>
    setExpandedTypes((current) =>
      current.includes(entityType) ? current.filter((t) => t !== entityType) : [...current, entityType]
    );

  const relatedTo = (programId: string) =>
    available.filter((entity) => (entity.relatedEntityIds ?? []).includes(programId));

  const unselectedRelatedTo = (programId: string) => relatedTo(programId).filter((entity) => !isChecked(entity));

  const selectRelatedTo = (programId: string) =>
    setSelected((current) => {
      const next = { ...current };

      relatedTo(programId).forEach((entity) => {
        const ids = next[entity.entityType] ?? [];

        if (!ids.includes(entity.masterEntityId)) {
          next[entity.entityType] = [...ids, entity.masterEntityId];
        }
      });

      return next;
    });

  const toggleAllOfType = (entityType: string) => {
    const filteredIds = availableOfType(entityType).map((entity) => entity.masterEntityId);
    const selectAll = !allSelectedOfType(entityType);

    setSelected((current) => {
      const ids = current[entityType] ?? [];

      return {
        ...current,
        [entityType]: selectAll
          ? [...ids, ...filteredIds.filter((id) => !ids.includes(id))]
          : ids.filter((id) => !filteredIds.includes(id)),
      };
    });
  };

  const handleClose = () => {
    setSelected({});
    setSaving(false);
    onClose();
  };

  const handleAdd = () => {
    setSaving(true);

    campfireMutation
      .run({
        url: `${apiUrl}/vm/enterprise/sub-accounts/stamp`,
        method: 'post',
        data: { subTenantId, selection: selected },
      })
      .then((res: any) => {
        if (!res.ok) {
          setSaving(false);
          setSnackbar({
            variant: 'error',
            open: true,
            message: res?.data?.message || 'Could not copy the selection',
          });
          return;
        }

        setSnackbar({
          variant: 'success',
          open: true,
          message: `Copied to ${subAccountName}`,
        });
        setSelected({});
        setSaving(false);
        onAdded();
        onClose();
      })
      .catch(() => {
        setSaving(false);
        setSnackbar({
          variant: 'error',
          open: true,
          message: 'Could not copy the selection',
        });
      });
  };

  if (!open) return null;

  return (
    <FullscreenDialog
      open
      close={handleClose}
      title='Add Entities'
      maxWidth='sm'
      fullWidth
      dialogActions={() => (
        <>
          <TabletButton onClick={handleClose} disabled={saving} style={{ marginRight: '8px' }}>
            Cancel
          </TabletButton>
          <TabletButton
            variant='contained'
            color='primary'
            onClick={handleAdd}
            disabled={saving || selectedCount === 0}
          >
            {saving ? 'Copying…' : `Copy ${selectedCount > 0 ? selectedCount : ''}`.trim()}
          </TabletButton>
        </>
      )}
    >
      <Box mt='20px'>
        <Typography variant='body2' className={classes.bodyPanel}>
          {`Choose what to copy into ${subAccountName}. Anything a selection depends on, such as a task's category, is copied with it.`}
        </Typography>

        <Box paddingTop='1rem'>
          <SearchField
            placeholder='Search entities'
            fullWidth
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          />
        </Box>

        {loading || !hasLoaded ? (
          <Box display='flex' justifyContent='center' padding='2rem 0'>
            <CircularProgress size={26} />
          </Box>
        ) : available.length === 0 ? (
          <Box paddingTop='1.5rem'>
            <Typography variant='body2' className={classes.bodyPanel}>
              {term === ''
                ? 'Everything in this account has already been copied across.'
                : `Nothing matches "${search.trim()}".`}
            </Typography>
          </Box>
        ) : (
          groupedTypes.map((entityType) => (
            <Box key={entityType}>
              <Box className={classes.sectionBar}>
                <Typography className={classes.sectionHeading}>{entityTypeHeading(entityType)}</Typography>
                <Button
                  size='small'
                  color='primary'
                  className={classes.selectAll}
                  onClick={() => toggleAllOfType(entityType)}
                >
                  {allSelectedOfType(entityType) ? 'Deselect all' : 'Select all'}
                </Button>
              </Box>
              <Divider />
              <List disablePadding>
                {visibleOfType(entityType).map((entity) => {
                  const TypeIcon = entityTypeIcon(entity.entityType);

                  return (
                    <ListItem key={entity.masterEntityId} button dense onClick={() => toggle(entity)} disableGutters>
                      <ListItemIcon style={{ minWidth: '40px' }}>
                        <Checkbox
                          edge='start'
                          color='primary'
                          checked={isChecked(entity)}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <TypeIcon className={classes.typeIcon} />
                      <ListItemText primary={entity.name} primaryTypographyProps={{ className: classes.name }} />
                      {entity.entityType === 'program' && unselectedRelatedTo(entity.masterEntityId).length > 0 && (
                        <Button
                          size='small'
                          color='primary'
                          className={classes.relatedButton}
                          onClick={(event: React.MouseEvent) => {
                            event.stopPropagation();
                            selectRelatedTo(entity.masterEntityId);
                          }}
                        >
                          {`Add ${unselectedRelatedTo(entity.masterEntityId).length} linked`}
                        </Button>
                      )}
                    </ListItem>
                  );
                })}
              </List>
              {hiddenCountOfType(entityType) > 0 && (
                <Button
                  size='small'
                  color='primary'
                  className={classes.selectAll}
                  onClick={() => toggleExpanded(entityType)}
                >
                  {`Show ${hiddenCountOfType(entityType)} more`}
                </Button>
              )}
              {isExpanded(entityType) && term === '' && availableOfType(entityType).length > COLLAPSED_LIMIT && (
                <Button
                  size='small'
                  color='primary'
                  className={classes.selectAll}
                  onClick={() => toggleExpanded(entityType)}
                >
                  Show less
                </Button>
              )}
            </Box>
          ))
        )}
      </Box>
    </FullscreenDialog>
  );
};
